import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import type { LabelGroupDetail } from '~settings/api/lib/schemas';
import { useLabelGroupEdit } from '~settings/api/mutations/labelGroupEdit';
import { useLabelGroupDetails } from '~settings/api/queries/labelGroup';
import { CTABlock } from '~settings/components/CTABlock';
import { SettingsPageHeader } from '~settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import type { LabelGroupFormSchema } from '~settings/modules/label-group/components/LabelGroupForm';
import { LabelGroupForm } from '~settings/modules/label-group/components/LabelGroupForm';
import { getMessageFromStatus } from '~settings/modules/label-group/helpers';
import { toDropdownList } from '~settings/utils/transformers';

const FORM_ID = 'editLabelGroupForm';

export function EditLabelGroupPage() {
  const { t } = useTranslation('settings');
  const { labelGroupId } = useParams();
  const navigate = useNavigate();

  const { mutate: submitData, isPending } = useLabelGroupEdit(
    labelGroupId ?? '',
    {
      onSuccess: (data) => {
        showToast({
          title: t('labelGroup.editSuccessMessage', {
            name: data.name,
            interpolation: { escapeValue: false },
          }),
          type: 'success',
        });

        navigate(SETTINGS_ROUTES.LABEL_GROUP_DETAIL(data.id));
      },
      onError: (error) => {
        const status = error?.response?.status;
        const message = getMessageFromStatus(status);

        showToast({
          title: t(message.title),
          description: t(message.description),
        });
      },
    },
  );

  const { isLoading, error, labelGroup } = useLabelGroupDetails(
    { labelGroupId: labelGroupId ?? '' },
    {
      select: (values: LabelGroupDetail) => ({
        ...values,
        notes: values.notes ?? '',
        labels: toDropdownList(values.labels) ?? [],
        users: toDropdownList(values.users ?? [], 'fullName'),
      }),
    },
  );

  const onSubmit = (data: LabelGroupFormSchema) => {
    const apiData = {
      name: data.name,
      notes: data.notes,
      labelIds: data.labels.map((label) => label.value),
    };

    submitData(apiData);
  };

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !labelGroup || !labelGroupId) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.LABEL_GROUP_LIST,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <article className="relative flex h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.LABEL_GROUP_LIST, label: 'labelGroup.list' },
          { to: '', label: labelGroup.name },
        ]}
        title={labelGroup.name}
      />

      <div className="grow overflow-y-auto pb-xl">
        <LabelGroupForm onSubmit={onSubmit} id={FORM_ID} values={labelGroup} />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_GROUP_LIST}>
            {t('buttons.cancel')}
          </Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          size="large"
          form={FORM_ID}
          disabled={isPending}
        >
          {t('buttons.saveChanges')}
        </Button>
      </CTABlock>
    </article>
  );
}

EditLabelGroupPage.displayName = 'EditLabelGroupPage';
